import { useRef, useState } from "react";
import VerbalCalibration, { VerbalCalibrationNotice, VerbalStartNotice, VerbalParamsNotice } from "./components/verbalCalibration";
import EarSelection from "../../components/earSelection/earSelection";
import VerbalSRTPanel from "./components/verbalSRTPanel";
import VerbalWRSPanel from "./components/verbalWRSPanel";
import VerbalResultPanel from "../../components/verbalResultPanel/verbalResultPanel";
import VerbalTestParamsModal from "../../modal/verbalTestParamsModal/verbalTestParamsModal";
import SendEmailResultModal from "../../modal/sendEmailResultModal/sendEmailResultModal";
import VerbalAnalysisModal from "../../modal/analysisModal/analysisModal";
import { getApiUrl } from "../../utils/getApiUrl";
import { fetchWithAuth } from "../../auth/auth";
import TonalParamsBadges from "../../components/tonalResultPanel/tonalParamsBadges";
import { useAuthUser } from "../../utils/useAuthUser";
import { useResultExport } from "../../utils/useResultExport";
import { logActivity } from "../../utils/logActivity";
import { canAnalyzeVerbalResult } from "../../utils/hearingResultAnalysis";
import type { Ear } from "../../type/tonalTest";
import type { AgeCategory, Gender, HeadphoneConnection, HeadphoneType } from "../../type/demographics";
import { useOverlayAnimation } from "../../utils/useOverlayAnimation";
import type {
  VerbalTestStep,
  VerbalEarResult,
  VerbalTestResultPayload,
  VerbalWordRecord,
  AddVerbalTestResultRequest
} from "../../type/verbalTest";
import "./verbalAudiometry.scss";

// dB levels array (must match the panels)
const dBLevels = Array.from({ length: 21 }, (_, i) => i * 5);

const emptyEarResult = (): VerbalEarResult => ({
  srt: null,
  wrs: null,
  srt_words: [],
  wrs_words: []
});

export default function VerbalAudiometry() {
  const user = useAuthUser();
  const [step, setStep]       = useState<VerbalTestStep>("start");
  const [ear, setEar]         = useState<Ear>("left");
  const [useMicrophone, setUseMicrophone] = useState(true);
  const [doWRS, setDoWRS] = useState(true);
  const [doSRT, setDoSRT] = useState(false);
  const [useSentences, setUseSentences] = useState(false);
  const [extendedWRS, setExtendedWRS] = useState(false);
  const [gender, setGender] = useState<Gender>("unspecified");
  const [ageCategory, setAgeCategory] = useState<AgeCategory>("unspecified");
  const [headphoneType, setHeadphoneType] = useState<HeadphoneType>("unspecified");
  const [headphoneConnection, setHeadphoneConnection] = useState<HeadphoneConnection>("unspecified");
  const [isParamsModalOpen, setIsParamsModalOpen] = useState(false);

  // Partial results accumulated during the test
  const [srtResults, setSrtResults] = useState<Record<Ear, number | null>>({ left: null, right: null });
  const [wrsResults, setWrsResults] = useState<Record<Ear, number | null>>({ left: null, right: null });
  const [earDetails, setEarDetails] = useState<Record<Ear, VerbalEarResult>>({
    left: emptyEarResult(),
    right: emptyEarResult()
  });

  const [finalPayload, setFinalPayload] = useState<VerbalTestResultPayload | null>(null);
  const [savedVerbalResultId, setSavedVerbalResultId] = useState<number | null>(null);
  const [isAnalysisModalOpen, setIsAnalysisModalOpen] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState<string | null>(null);
  const hasSavedRef = useRef(false);

  const {
    isExportMenuOpen,
    setIsExportMenuOpen,
    isExporting,
    exportFormat,
    exportMenuRef,
    resultPanelRef,
    handleExport,
    captureResultPanel
  } = useResultExport({
    panelSelector: ".verbal-results",
    actionsClassName: "result-header__actions",
    getFileBaseName: () => `verbal-audiometry-result-${new Date().toISOString().slice(0, 10)}`,
    onExported: (format) => logActivity("download_result", window.location.pathname, `Pobrano wyniki audiometrii słownej jako ${format.toUpperCase()}`, savedVerbalResultId)
  });

  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const {
    tonePanelOverlay,
    setTonePanelOverlay,
    animateTonePanelToSource,
    clearOverlayFrame,
    clearFinishTransitionTimeout,
    testPanelSlotRef,
    testPanelShellRef,
    isTonePanelFloating,
    floatingTonePanelStyle,
  } = useOverlayAnimation(step === "srt" || step === "wrs");

  // ── Save result to backend ────────────────────────────────────────────────
  const saveResult = async (payload: VerbalTestResultPayload): Promise<number | null> => {
    try {
      const body: AddVerbalTestResultRequest = {
        iduser:     user?.iduser ?? null,
        ip_address: null,
        web_agent:  navigator.userAgent,
        result:     JSON.stringify(payload),
        test_params: JSON.stringify({ useMicrophone, gender, ageCategory, headphoneType, headphoneConnection })
      };
      const res  = await fetchWithAuth(getApiUrl("verbalresult/add"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (data?.idverbalresult) { setSavedVerbalResultId(data.idverbalresult); return data.idverbalresult as number; }
    } catch (err) {
      console.error("Failed to save verbal test result:", err);
    }
    return null;
  };

  // ── SRT phase completion ───────────────────────────────────────────────────
  const handleSRTComplete = async (completedEar: Ear, srt: number, words: VerbalWordRecord[]) => {
    const nextSrt = { ...srtResults, [completedEar]: srt };
    const nextDetails: Record<Ear, VerbalEarResult> = {
      ...earDetails,
      [completedEar]: { ...earDetails[completedEar], srt, srt_words: words }
    };
    setSrtResults(nextSrt);
    setEarDetails(nextDetails);

    const otherEar: Ear = completedEar === "left" ? "right" : "left";

    if (nextSrt[otherEar] === null) {
      // Test other ear SRT next
      setEar(otherEar);
      // Stay in "srt" step — useEffect will detect ear change
    } else if (doWRS) {
      // Both SRTs done → animate panel back, then move to WRS
      animateTonePanelToSource(() => {
        setTonePanelOverlay(null);
        setEar("left");
        setStep("wrs");
      });
    } else {
      // Both SRTs done, WRS skipped → finalize
      const payload: VerbalTestResultPayload = {
        srt: { left: nextSrt.left, right: nextSrt.right },
        wrs: { left: null, right: null },
        word_results: {
          left:  nextDetails.left,
          right: nextDetails.right
        }
      };
      let resultId: number | null = null;
      if (!hasSavedRef.current) {
        hasSavedRef.current = true;
        setFinalPayload(payload);
        resultId = await saveResult(payload);
      }
      logActivity("end_test", window.location.pathname, "Zakończono audiometrię słowną SRT", resultId);
      animateTonePanelToSource(() => {
        setTonePanelOverlay(null);
        setStep("result");
      });
    }
  };

  // ── WRS phase completion ───────────────────────────────────────────────────
  const handleWRSComplete = async (completedEar: Ear, wrs: number, words: VerbalWordRecord[]) => {
    const nextWrs = { ...wrsResults, [completedEar]: wrs };
    const nextDetails: Record<Ear, VerbalEarResult> = {
      ...earDetails,
      [completedEar]: { ...earDetails[completedEar], wrs, wrs_words: words }
    };
    setWrsResults(nextWrs);
    setEarDetails(nextDetails);

    const otherEar: Ear = completedEar === "left" ? "right" : "left";

    if (nextWrs[otherEar] === null) {
      // Test other ear WRS next
      setEar(otherEar);
      // Stay in "wrs" step
    } else {
      // Both WRS done → finalize
      const payload: VerbalTestResultPayload = {
        srt: { left: srtResults.left, right: srtResults.right },
        wrs: { left: nextWrs.left,    right: nextWrs.right },
        word_results: {
          left:  nextDetails.left,
          right: nextDetails.right
        }
      };
      let resultId: number | null = null;
      if (!hasSavedRef.current) {
        hasSavedRef.current = true;
        setFinalPayload(payload);
        resultId = await saveResult(payload);
      }
      logActivity("end_test", window.location.pathname, "Zakończono audiometrię słowną WRS", resultId);
      animateTonePanelToSource(() => {
        setTonePanelOverlay(null);
        setStep("result");
      });
    }
  };

  // ── Ear selection → starts SRT or WRS for that ear ────────────────────────
  const handleEarSelect = (selected: Ear) => {
    setEar(selected);
    const params = `useMicrophone=${useMicrophone}, doWRS=${doWRS}, doSRT=${doSRT}, useSentences=${useSentences}`;
    logActivity("start_test", window.location.pathname, `Rozpoczęto audiometrię słowną: ${params}`, undefined);
    setStep(doSRT ? "srt" : "wrs");
  };

  // ── Reset ─────────────────────────────────────────────────────────────────
  const handleNewTest = () => {
    clearOverlayFrame();
    clearFinishTransitionTimeout();
    setTonePanelOverlay(null);
    window.speechSynthesis.cancel();
    setSrtResults({ left: null, right: null });
    setWrsResults({ left: null, right: null });
    setEarDetails({ left: emptyEarResult(), right: emptyEarResult() });
    setFinalPayload(null);
    hasSavedRef.current = false;
    setAiAnalysis(null);
    setEar("left");
    setStep("start");
  };

  const handleCancel = async (partialWords: VerbalWordRecord[]) => {
    if (!hasSavedRef.current) {
      // Merge partial words from the in-progress panel into earDetails
      const mergedDetails: Record<Ear, VerbalEarResult> = {
        left:  { ...earDetails.left },
        right: { ...earDetails.right },
      };
      if (partialWords.length > 0) {
        if (step === "srt") {
          mergedDetails[ear] = { ...mergedDetails[ear], srt_words: partialWords };
        } else if (step === "wrs") {
          mergedDetails[ear] = { ...mergedDetails[ear], wrs_words: partialWords };
        }
      }
      const hasData =
        srtResults.left !== null || srtResults.right !== null ||
        wrsResults.left !== null || wrsResults.right !== null ||
        mergedDetails.left.srt_words.length > 0 || mergedDetails.left.wrs_words.length > 0 ||
        mergedDetails.right.srt_words.length > 0 || mergedDetails.right.wrs_words.length > 0;
      if (hasData) {
        hasSavedRef.current = true;
        const payload: VerbalTestResultPayload = {
          srt: { left: srtResults.left, right: srtResults.right },
          wrs: { left: wrsResults.left, right: wrsResults.right },
          word_results: mergedDetails
        };
        const resultId = await saveResult(payload);
        logActivity("end_test", window.location.pathname, "Przerwano audiometrię słowną (wynik częściowy)", resultId);
      } else {
        logActivity("end_test", window.location.pathname, "Przerwano audiometrię słowną", undefined);
      }
    }
    handleNewTest();
  };

  // Determine WRS volume index from the current ear's SRT
  const currentSrtDb = srtResults[ear] ?? 60;
  const srtVolIdx    = dBLevels.indexOf(currentSrtDb);
  // If SRT was not found in dBLevels (shouldn't happen), clamp
  const safeSrtDb    = srtVolIdx >= 0 ? currentSrtDb : 60;

  return (
    <div className={`hearing-test hearing-test--verbal${isTonePanelFloating ? " hearing-test--tone-focus" : ""}`}>
      {isTonePanelFloating && (
        <div
          className={`hearing-test__tone-backdrop${tonePanelOverlay?.animateToTarget ? " hearing-test__tone-backdrop--visible" : ""}`}
          aria-hidden="true"
        />
      )}
      <div className="hearing-test__content">
        {/* ── Main content ──────────────────────────────────── */}
        <div className="hearing-test__right">

          {step === "start" && (
            <VerbalStartNotice onConfirmed={() => setStep("params")} />
          )}

          {step === "params" && (
            <VerbalParamsNotice
              onConfirmed={() => setStep("volume")}
              onOpenParamsModal={() => setIsParamsModalOpen(true)}
              onNewTest={handleNewTest}
              useMicrophone={useMicrophone}
              doWRS={doWRS}
              doSRT={doSRT}
              useSentences={useSentences}
              gender={gender}
              ageCategory={ageCategory}
              headphoneType={headphoneType}
              headphoneConnection={headphoneConnection}
              onChangeGender={setGender}
              onChangeAgeCategory={setAgeCategory}
              onChangeHeadphoneType={setHeadphoneType}
              onChangeHeadphoneConnection={setHeadphoneConnection}
            />
          )}

          {step === "volume" && (
            <VerbalCalibrationNotice onConfirmed={() => setStep("calibration")} onNewTest={handleNewTest} />
          )}

          {step === "calibration" && (
            <VerbalCalibration onDone={() => setStep("ear")} onNewTest={handleNewTest} />
          )}

          {step === "ear" && (
            <EarSelection onSelect={handleEarSelect} onNewTest={handleNewTest} />
          )}

          {(step === "srt" || step === "wrs") && (
            <div
              ref={testPanelSlotRef}
              className="hearing-test__test-panel-slot"
              style={isTonePanelFloating ? { height: `${tonePanelOverlay!.placeholderHeight}px` } : undefined}
            >
              <div
                ref={testPanelShellRef}
                className={`hearing-test__test-panel-shell${isTonePanelFloating ? " hearing-test__test-panel-shell--floating" : ""}`}
                style={floatingTonePanelStyle}
              >
                {step === "srt" && (
                  <VerbalSRTPanel
                    key={`srt-${ear}`}
                    ear={ear}
                    useMicrophone={useMicrophone}
                    useSentences={useSentences}
                    onComplete={handleSRTComplete}
                    onCancel={handleCancel}
                    user={user}
                  />
                )}
                {step === "wrs" && (
                  <VerbalWRSPanel
                    key={`wrs-${ear}`}
                    ear={ear}
                    srtDb={safeSrtDb}
                    useMicrophone={useMicrophone}
                    useSentences={useSentences}
                    extendedWRS={extendedWRS}
                    onComplete={handleWRSComplete}
                    onCancel={handleCancel}
                    user={user}
                  />
                )}
              </div>
            </div>
          )}

          {step === "result" && finalPayload && (
            <>
              <VerbalResultPanel
                result={finalPayload}
                resultPanelRef={resultPanelRef}
                onNewTest={handleNewTest}
                subtitle={<TonalParamsBadges params={{ gender, ageCategory, headphoneType, headphoneConnection }} />}
                exportMenuRef={exportMenuRef}
                isExportMenuOpen={isExportMenuOpen}
                isExporting={isExporting}
                exportFormat={exportFormat}
                isExportDisabled={isExporting}
                showAnalysisButton={true}
                isAnalysisDisabled={isExporting}
                onToggleExportMenu={() => setIsExportMenuOpen(prev => !prev)}
                onExport={(format) => void handleExport(format)}
                onAnalyze={() => {
                  if (!finalPayload || !canAnalyzeVerbalResult(finalPayload)) return;
                  logActivity("analyze_results", window.location.pathname, "Otwarto analizę wyników", savedVerbalResultId);
                  setIsAnalysisModalOpen(true);
                }}
                onEmailResult={() => { setIsExportMenuOpen(false); setIsEmailModalOpen(true); }}
              />

              <VerbalAnalysisModal
                isOpen={isAnalysisModalOpen}
                onClose={() => setIsAnalysisModalOpen(false)}
                parsedResult={finalPayload}
                demographics={{ ageCategory, headphoneType }}
                id={savedVerbalResultId}
                cachedAnalysis={aiAnalysis}
                onAnalysisSaved={(_id, analysis) => setAiAnalysis(analysis)}
              />
            </>
          )}
        </div>
      </div>

      <SendEmailResultModal
        isOpen={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        captureResultPanel={captureResultPanel}
        fileBaseName={`verbal-audiometry-result-${new Date().toISOString().slice(0, 10)}`}
        idverbalresult={savedVerbalResultId}
      />

      <VerbalTestParamsModal
        isOpen={isParamsModalOpen}
        onClose={() => setIsParamsModalOpen(false)}
        useMicrophone={useMicrophone}
        onToggleMicrophone={() => setUseMicrophone(prev => !prev)}
        doWRS={doWRS}
        onToggleDoWRS={() => setDoWRS(prev => !prev)}
        doSRT={doSRT}
        onToggleDoSRT={() => setDoSRT(prev => !prev)}
        useSentences={useSentences}
        onToggleUseSentences={() => setUseSentences(prev => !prev)}
        extendedWRS={extendedWRS}
        onToggleExtendedWRS={() => setExtendedWRS(prev => !prev)}
        gender={gender}
        ageCategory={ageCategory}
        onChangeGender={setGender}
        onChangeAgeCategory={setAgeCategory}
      />
    </div>
  );
}
